<template>
  <VTDropdownPill
    :disable-save-button="selected.length < 1"
    :is-enabled="!disabled"
    @confirm="saveChanges"
    @clear="reset"
    @on-click-outside="discardChanges"
  >
    <template #dropDownHeader>
      <VTPill label="Segment">
        <template #values>
          <div :class="$style.wrapper">
            <div>
              <Chip
                v-for="segment in displayedSegments"
                :key="segment.id"
                :class="$style.chipLimited"
                :label="segment.name"
              />
              <Chip v-if="displayAdditionalItemsChip" :label="`+${hiddenElements}`" />
            </div>
          </div>
        </template>
      </VTPill>
    </template>
    <template #dropdown-content>
      <VTMultiSelect
        :has-add-section="false"
        :enable-search="false"
        :items="segments"
        :selected-items="selectedItems"
        :has-action-section="false"
        :selection-key="'id'"
        :allow-deletion="false"
        @toggle-selection="handleUpdate"
        @discard-changes="discardChanges"
        @save-changes="saveChanges"
      />
      <div v-if="selected.length < 1">
        <p :class="$style.invalidText">At least one segment should be selected</p>
      </div>
    </template>
  </VTDropdownPill>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { PropType } from "vue";
import Chip from "primevue/chip";
import VTDropdownPill from "@/components/VTDropdownPill";
import VTMultiSelect from "@/components/VTMultiSelect";
import VTPill from "@/components/VTPill";

interface Segment {
  id: string;
  name: string;
}

const props = defineProps({
  segments: {
    type: Array as PropType<Segment[]>,
    required: false,
    default: () => [],
  },
  modelValue: {
    type: Array as PropType<Array<string>>,
    required: true,
    default: () => [],
  },
  maxVisibleItems: {
    type: Number,
    default: 2,
  },
  defaultSelected: {
    type: String,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update"]);
const selected = ref(props.modelValue);
const selectedItems = computed(() =>
  selected.value.map((segment) => {
    const selectedSegment = props.segments.find((item) => item.id === segment);
    return {
      id: segment,
      name: selectedSegment?.name,
      selected: true,
    };
  })
);

const isSelected = (segment: Segment) => props.modelValue?.find((item) => item === segment.id);
const selectedSegment = computed(() => props.segments.filter(isSelected));
const displayAdditionalItemsChip = computed(() => selectedSegment.value.length > props.maxVisibleItems);
const hiddenElements = computed(() => selected.value.length - props.maxVisibleItems);
const displayedSegments = computed(() => selectedSegment.value.slice(0, props.maxVisibleItems));
watch(
  () => props.modelValue,
  (newValue) => {
    selected.value = newValue;
  }
);

const handleUpdate = (toggleItem: unknown) => {
  const toggledSegment = toggleItem as Segment;
  // look for the item in the selected items
  const selectedIndex = selected.value.findIndex((segment) => segment === toggledSegment.id);
  if (selectedIndex !== -1) {
    selected.value = selected.value.filter((item) => item !== toggledSegment.id);
  } else {
    selected.value = [toggledSegment.id];
  }
};

const saveChanges = () => {
  emit("update", selected.value);
};

const reset = () => {
  selected.value = props.defaultSelected ? [props.defaultSelected] : [];
  emit("update", selected.value);
};
const discardChanges = () => {
  selected.value = props.modelValue;
};
</script>
<style module>
.wrapper {
  min-width: 180px;
  max-width: 280px;
  display: flex;
  flex-direction: row;
  overflow-x: hidden;
  align-items: center;
  gap: 8px;
}
.chipLimited {
  margin: 0 4px;
}
.chipLimited div {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.invalidText {
  color: #f56b6b;
  margin: 0;
  padding: 0;
}
</style>
